import { Observable } from 'rxjs';
import { ScrServiceManager } from './servicemanager.service';
import { TranslateService } from '@ngx-translate/core';
import { v4 as uuid } from 'uuid';
import { HttpResponse } from '@angular/common/http';

import { saveAs } from 'file-saver-es';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';

export class ScrMessage {

  // messages can have a qualifier which is used by the application in the event
  // a given error is returned.  For example, if a payment is over a limit, return
  // what that limit is for processing.

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public data?: any;
  constructor(public code: string, public message: string, public field?: string, public type?: string) {

  }
}

/**
 * Class class for responses from SCR APIs.  We've standardized
 * on an array of error messages instead of a single message.
 */
export class ScrBaseResponse {
  constructor(public status: string, public messages: ScrMessage[]) {

  }
}

/**
 * The base interface for responses from the short codes
 * back end including a status string and optional array of messages.
 * 
 * No additional properties should be added here.  Feel free to
 * extend the interface for API specifics.
 */
export interface IScrBaseResponse {
  status: string;
  messages: ScrMessage[];
}

export interface ScrBaseResponseWithData<T> {
  status: string;
  messages: ScrMessage[];
  data: T;
}

export class ScrBaseService {

  static phoneUtil = new PhoneNumberUtil();

  private _translate?: TranslateService;

  constructor(private ssm: ScrServiceManager) {

  }

  protected get translate(): TranslateService {
    if (!this._translate) {
      this._translate = ScrServiceManager.injector!.get(TranslateService);
    }
    return this._translate;
  }

  public nobusy<T>(f: () => Observable<T>) {
    this.ssm.nobusy = true;
    return f();
  }

  public busy<T>(f: () => Observable<T>) {
    return f();
  }

  static isInvalidShortCode(shortCode: string): boolean {
    const sc = ScrBaseService.shortCodeToNumeric(shortCode);
    const start = sc.charAt(0);
    return sc.length < 5 || sc.length > 6 || start === "1" || start === "0";
  }

  static shortCodeToNumeric(shortcode: string): string {
    if (shortcode === undefined || shortcode === '') {
      return '';
    }
    const len = shortcode.length;
    const lower = shortcode.toLowerCase();
    let csc = '';
    for (let i = 0; i < len; i++) {
      const c = lower.charAt(i);
      if (c >= "0" && c <= "9") csc += c;
      else if (c >= "a" && c <= "c") csc += "2";
      else if (c >= "d" && c <= "f") csc += "3";
      else if (c >= "g" && c <= "i") csc += "4";
      else if (c >= "j" && c <= "l") csc += "5";
      else if (c >= "m" && c <= "o") csc += "6";
      else if (c >= "p" && c <= "s") csc += "7";
      else if (c >= "t" && c <= "v") csc += "8";
      else if (c >= "w" && c <= "z") csc += "9";
      else csc += c;
    }
    if (csc.length === 0) return shortcode;
    else return csc;
  }

  functionUrlBody(action: string, data?: object): object {
    const rv: object = {
      action,
      requestId: uuid()
    };
    if (data) {
      Object.assign(rv, data);
    }
    return rv;
  }

  processBlobResponseDownload(rsp: HttpResponse<Blob>, type = 'application/octet-stream', overrideFileName?: string): boolean {
    if (rsp.body) {
      const blob = new Blob([rsp.body], { type });
      if (blob.size > 0) {
        const disposition = rsp.headers.get('content-disposition');
        let fileName = disposition ? `${disposition.split('filename=')[1].split(';')[0]}` : undefined;
        if (fileName) {
          fileName = fileName.replace(/['"]+/g, '');
        }
        if (overrideFileName) {
          fileName = overrideFileName;
        }

        // some parts of the application had a check to be sure that the file
        // extension was pdf when type was pdf.
        if ((type === 'application/pdf') && !fileName?.endsWith('.pdf')) {
          fileName = fileName + '.pdf';
        }
        saveAs(blob, fileName);
        return true;
      }
    }
    return false;
  }


  static stringInject(message: string, values: (string | number)[]) {
    if (values && values.length > 0) {
      values.forEach((v,i) => {
        let vv;
        if (typeof v === 'number') {
          vv = `${v}`;
        }
        else {
          vv = v;
        }
        message = message.replaceAll(`{${i}}`, vv);
      });
    }
    return message;
  }



  /**
   * A custom replacer that handles phone number conversion.
   * The replacer is used for JSON.stringify to generate the
   * content sent to the back end.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static phoneReplacer(key: string, value: any) {
    
    // Paul found a case where billingPhone was an object but not one with e164Number.

    if (key && value && key.toLowerCase().endsWith('phone') && typeof value === 'object') {
      if (value.e164Number) {
        return value.e164Number;
      }
      else if (value.number) {
        try {
          const p = ScrBaseService.phoneUtil.parse(value.number, value.countryCode);
          return ScrBaseService.phoneUtil.format(p, PhoneNumberFormat.E164);
        }
        catch (e) {
          console.warn(e);
          return value.number;
        }
      }
    }
    return value;
  }

  /**
   * Safely stringify a recursive JSON structure with circular references discarded.
   * @param obj - object to stringify using JSON.stringify
   * @returns stringified version.
   */
    public static jsonStringifyRecursive(obj: object): string {
      const cache = new Set();
      return JSON.stringify(obj, (key, value) => {
          if (typeof value === 'object' && value !== null) {
              if (cache.has(value)) {
                  // Circular reference found, discard key
                  return;
              }
              // Store value in our collection
              cache.add(value);
          }
          return value;
      });
    }

}
